// The map breaks without this
import 'leaflet/dist/leaflet.css';

import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Button,
  Card,
  CardBody,
  Checkbox,
  Code,
  Divider,
  Flex,
  Input,
  Link,
  Spinner,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Tfoot,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { getAsset } from '@pelicargo/tenants';
import { TSAirport } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import { kmToMi, prettyCurrency } from '@pelicargo/utils';
import { format } from 'date-fns';
import leaflet, { featureGroup } from 'leaflet';
import { GeodesicLine } from 'leaflet.geodesic';
import { omit } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, Marker, TileLayer, Tooltip, useMap } from 'react-leaflet';
import { useParams } from 'react-router-dom';
import { useLocalStorage } from 'usehooks-ts';

import { trpc, TrpcOutputs } from '../config/trpc';
import { TypesenseClient, useTypesense } from '../hooks/useTypesense';
import { calculate } from '../utils/calculator';
import { distance } from '../utils/gis';

export const Pelicalc = () => {
  const { typesenseClient } = useTypesense({}, false);
  const { initialQuery } = useParams();

  return (
    <Show if={!!typesenseClient}>
      <Calculator initialQuery={initialQuery} typesense={typesenseClient} />
    </Show>
  );
};

type DistanceCalculator = (airport: TSAirport, pos: number) => number;

const redMarker = leaflet.icon({
  iconUrl: getAsset('marker-red.png'),
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  tooltipAnchor: [16, -28],
  shadowSize: [41, 41],
});

const blueMarker = leaflet.icon({
  iconUrl: getAsset('marker.png'),
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  tooltipAnchor: [16, -28],
  shadowSize: [41, 41],
});

// Used for displaying airport distances
const prettyDistance = (meters: number) => {
  if (!meters) return '-';
  const km = meters / 1000;
  return `${kmToMi(km).toFixed(1)}mi (${km.toFixed(1)}km)`;
};

// Removes all polylines from the map, used when someone directly adds/removes airports without a full redraw
const clearLines = (map: leaflet.Map & { _layers: any }) => {
  for (const i in map._layers) {
    if (map._layers[i]._path != undefined) {
      try {
        map.removeLayer(map._layers[i]);
      } catch (e) {
        console.log('problem with ' + e + map._layers[i]);
      }
    }
  }
};

// Calculate marker points and the line of an airport, complicated due to the antimeridian
// I wish the earth was flat
const calculateMapPoints = (positions: [number, number][], map: leaflet.Map) => {
  if (!map) {
    return {
      positions: [],
      line: null,
      distances: [],
    };
  }
  clearLines(map as any);
  if (positions?.length === 1) {
    return {
      positions,
      line: null,
      distances: [],
    };
  }

  const line = new GeodesicLine(positions, {
    weight: 5,
    wrap: false,
  });
  const ret = {
    positions: [],
    line: line.addTo(map),
    distances: [],
  };
  // Use an internal function of leaflet.geodesic to determine each endpoint of the string
  const segments = positions
    .slice(1)
    .map((bound, i) => [positions[i], bound])
    .map((latlng) =>
      line.geom.wrapMultiLineString([[new leaflet.LatLng(...latlng[0]), new leaflet.LatLng(...latlng[1])]]),
    );
  // Keep track of how many times we've lapped the earth, and in what direction
  let wrapping = 0;

  // Don't double up positions
  const handled = new Set();

  segments.forEach(([segment]) => {
    // Calculate the start and endpoint of a line segment, adding/subtracting lng to keep it consistent
    // Also am I insane or should it be lat? ("latitude fatitude" is the mnemonic I remember)
    const start = {
      lng: segment[0].lng + wrapping * 360,
      lat: segment[0].lat,
    };
    const end = {
      lng: segment[1].lng + wrapping * 360,
      lat: segment[1].lat,
    };
    // If it extends past the normal bounds, increment/decrement the wrap
    if (segment[1].lng < -180) {
      wrapping--;
    } else if (segment[1].lng > 180) {
      wrapping++;
    }
    // Add each modified position to what we're returning
    for (const point of [start, end]) {
      // Bad hash
      const j = JSON.stringify(point);
      if (!handled.has(j)) {
        handled.add(j);
        ret.positions.push([point.lat, point.lng]);
        if (ret.positions.length > 1) {
          // Give the distance between this and the last airport
          ret.distances.push(
            line.geom.distance(new leaflet.LatLng(start.lat, start.lng), new leaflet.LatLng(end.lat, end.lng)),
          );
        }
      }
    }
  });
  // Zoom the map out to fit it all
  map.fitBounds(ret.positions);
  return ret;
};

const TableRow = ({
  airport,
  i,
  distanceCalculator,
}: {
  airport: TSAirport;
  i: number;
  distanceCalculator: DistanceCalculator;
}) => {
  return (
    <Tr fontWeight={airport.airport_main_gateway ? 'bold' : 'normal'}>
      <Td py={0} px={2} w="2em">
        {airport.airport_id}
      </Td>
      <Td py={0} px={2} w="2em">
        {airport.airport_country}
      </Td>
      <Td py={0} px={2}>
        {airport.airport_name}
      </Td>
      <Td py={0} px={2}>
        {prettyDistance(distanceCalculator(airport, i))}
      </Td>
    </Tr>
  );
};

const AirportTable = ({
  airports,
  distanceCalculator,
}: {
  airports: TSAirport[];
  distanceCalculator: DistanceCalculator;
}) => {
  const [expanded, setExpanded] = useState(false);
  const [onlyLarge, setOnlyLarge] = useLocalStorage('calculator-large-airports', false);

  const displayAirport = (airport: TSAirport, i: number) => (
    <TableRow key={i} airport={airport} i={i} distanceCalculator={distanceCalculator} />
  );
  const filtered = airports.filter((airport) => (onlyLarge ? airport.airport_main_gateway : true));

  const buttonMsg = expanded ? 'Collapse' : `Expand (${filtered.length - 8} more)`;

  return (
    <Table fontWeight="bold" variant="sensible" fontSize="small" w="full">
      <Tbody>
        {filtered
          .slice(0, expanded ? undefined : 8)

          .map(displayAirport)}
        <Tr>
          <Show if={filtered.length >= 8}>
            <Td colSpan={3} px={0} py={0}>
              <Button onClick={() => setExpanded(!expanded)} width="full" borderRadius="0">
                {buttonMsg}
              </Button>
            </Td>
          </Show>

          <Td padding="0" colSpan={filtered.length >= 8 ? 1 : 4}>
            <Checkbox
              padding="2"
              isChecked={onlyLarge}
              onChange={() => {
                setOnlyLarge(!onlyLarge);
              }}
            >
              Only Large?
            </Checkbox>
          </Td>
        </Tr>
      </Tbody>
    </Table>
  );
};

const AirportPath = ({ airports }: { airports: TSAirport[] }) => {
  const [map, setMap] = useState(null);
  const bounds = airports.map((airport) => airport.airport_latlng);

  if (airports.length === 0) return;

  // Despite the function being a bit long, it's just math and recalculations usually return early.
  // Leaflet's map is also very finicky with state so memoizing is a pain. If you feel this is a huge issue,
  // I invite you to figure it out, because I sure couldn't
  const calculatedInfo = calculateMapPoints(bounds, map);

  return (
    <Flex flexDir="column" width="full">
      {/* Tables provide the nicest consistency */}
      <Table fontWeight="bold" variant="sensible" fontSize="small">
        <AirportTable airports={airports} distanceCalculator={(_, i) => calculatedInfo.distances[i - 1]} />
      </Table>
      <Flex width="full" marginTop={3}>
        <MapContainer
          center={bounds.length === 1 ? bounds[0] : undefined}
          zoom={5}
          scrollWheelZoom={false}
          style={{ height: 400, width: '100%' }}
          ref={setMap}
        >
          <TileLayer
            attribution="&copy; Powered by Esri. ZIP from Geocodes"
            url="http://services.arcgisonline.com/arcgis/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}"
          />
          {calculatedInfo.positions?.map((point, i) => <Marker icon={blueMarker} position={point} key={i}></Marker>)}
          <MapRecalculator markers={calculatedInfo.positions} />
        </MapContainer>
      </Flex>
    </Flex>
  );
};

const MapRecalculator = ({ markers }: { markers: any[] }) => {
  const map = useMap();
  useEffect(() => {
    map.invalidateSize();
    const layers = [];
    map.eachLayer((layer) => {
      // Filters out only markers
      if (layer?.['dragging']) {
        layers.push(layer);
      }
    });
    if (!layers?.length || layers.length === 1) {
      return;
    }
    map.fitBounds(featureGroup(layers).getBounds());
  }, [markers, map]);
  return <></>;
};

// For nearby airports. Different enough from AirportPath
const NearbyAirports = ({ airport, nearby }: { airport: TSAirport; nearby: TSAirport[] }) => {
  const [onlyLarge] = useLocalStorage('calculator-large-airports', false);

  const markers = nearby
    .filter((ap) => (onlyLarge ? ap.airport_main_gateway : true))
    .filter((nearbyAirport) => nearbyAirport.airport_name != airport.airport_name)
    .map((nearbyAirport, i) => (
      <Marker key={i} position={nearbyAirport?.airport_latlng} icon={blueMarker}>
        <Tooltip>
          {nearbyAirport.airport_id} - {nearbyAirport.airport_name}
        </Tooltip>
      </Marker>
    ));

  return (
    <Flex flexDir="column" width="full" gap="2">
      <Flex width="full" marginTop={3}>
        <MapContainer
          center={airport?.airport_latlng}
          zoom={6}
          scrollWheelZoom={false}
          style={{
            height: 400,
            width: '100%',
          }}
        >
          <TileLayer
            attribution="&copy; Powered by Esri. ZIP from Geocodes"
            url="http://services.arcgisonline.com/arcgis/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}"
          />
          <Marker position={airport?.airport_latlng} icon={redMarker} />
          {markers}
          <MapRecalculator markers={markers} />
        </MapContainer>
      </Flex>
      <AirportTable
        airports={nearby}
        distanceCalculator={(nearbyAirport) => distance(airport.airport_latlng, nearbyAirport.airport_latlng)}
      />
    </Flex>
  );
};

type EstimateInfo = TrpcOutputs['priceEstimate']['info'][string];

const PriceRow = ({ type, value }: { type: string; value: EstimateInfo[keyof EstimateInfo] }) => {
  return (
    <Tr key={type} whiteSpace="nowrap">
      <Td>{type}:</Td>
      <Td color="green">
        {prettyCurrency(value.min)} ({value.lowestAirline})
      </Td>
      <Td>{prettyCurrency(value.average)}</Td>
      <Td color="red">{prettyCurrency(value.max)}</Td>
      <Td>
        <Link target="_blank" href={`/requests/${value.lowestRequest}`}>
          R{value.lowestRequest}
        </Link>
      </Td>
      <Td color="primary">({value.totalRequests ?? 0})</Td>
    </Tr>
  );
};

const PriceEstimateDisplay = ({ estimate }: { estimate: TrpcOutputs['priceEstimate'] }) => {
  const order = useMemo(() => {
    return Object.keys(estimate.info).toSorted(
      (a, b) => estimate.info[a]['_SUMMARY'].min - estimate.info[b]['_SUMMARY'].min,
    );
  }, [estimate]);

  if (!estimate.lowest) {
    return (
      <Flex>
        Couldn't find any data within the weight break {estimate?.weight_break?.min} -&gt;{' '}
        {estimate?.weight_break?.max === Number.MAX_SAFE_INTEGER ? '∞' : estimate?.weight_break?.max}
      </Flex>
    );
  }

  return (
    <Flex direction="column" w="100%">
      <Flex gap={2}>
        <Flex>
          CW: {estimate?.weight_break?.min} -&gt;{' '}
          {estimate?.weight_break?.max === Number.MAX_SAFE_INTEGER ? '∞' : estimate?.weight_break?.max}
        </Flex>
        <Flex>
          Lowest rate: {prettyCurrency(estimate.lowest.min)} via {estimate.lowest.lowestAirline}
        </Flex>
        <Link target="_blank" href={`/requests/${estimate.lowest.lowestRequest}`}>
          (R
          {estimate.lowest.lowestRequest})
        </Link>
      </Flex>
      <Flex direction="row" flexWrap="wrap" gap="2">
        {order.map((airport) => {
          const stats = estimate.info[airport];
          return (
            <Card key={airport} w="30em">
              <CardBody>
                <Flex gap={2} direction="column">
                  <Text fontWeight="bold">
                    {airport} - from {prettyCurrency(stats['_SUMMARY'].min)}
                  </Text>
                  <Divider />
                  <Table variant="extraCompact" fontFamily="Fira Code">
                    <Tbody>
                      {Object.entries(omit(stats, '_SUMMARY')).map(([type, value]) => (
                        <PriceRow key={type} type={type} value={value} />
                      ))}
                      <Tr height={2}>
                        <Td></Td>
                      </Tr>
                    </Tbody>
                    <Tfoot>
                      <PriceRow type={'Summary'} value={stats['_SUMMARY']} />
                    </Tfoot>
                  </Table>
                </Flex>
              </CardBody>
            </Card>
          );
        })}
      </Flex>
    </Flex>
  );
};

const TrackingInfo = ({ info }: { info: TrpcOutputs['trackAwbAdHoc'] }) => {
  if ('directUrl' in info) {
    return (
      <Text>
        Provider unsupported. Track here:{' '}
        <Link href={info.directUrl} target="_blank" fontWeight="bold">
          {info.directUrl}
        </Link>
      </Text>
    );
  }
  return (
    <Table variant="compact">
      <Thead>
        <Tr>
          <Th>From</Th>
          <Th>To</Th>
          <Th>Weight</Th>
          <Th>Pieces</Th>
        </Tr>
        <Tr>
          <Td>{info.origin}</Td>
          <Td>{info.destination}</Td>
          <Td>{info?.initialWeight ?? '-'}</Td>
          <Td>{info?.initialPieces ?? '-'}</Td>
        </Tr>
      </Thead>
      <Tbody>
        <Tr>
          <Th>Time</Th>
          <Th>Location</Th>
          <Th colSpan={2}>Info</Th>
        </Tr>
        {info.history.map((event, i) => (
          <Tr key={i}>
            <Td fontSize="xs">{format(event.timestamp, 'yy/dd/MM hh:MM')}</Td>
            <Td fontSize="xs">{event.location}</Td>
            <Td fontSize="xs" colSpan={2}>
              {event.info}
            </Td>
          </Tr>
        ))}
      </Tbody>
    </Table>
  );
};

const Help = () => {
  // Some people may want the help to always be open. Some may not
  const [helpOpen, setHelpOpen] = useLocalStorage('calculatorHelpOpen', true);

  return (
    <Accordion allowMultiple defaultIndex={helpOpen ? [0] : []} onChange={() => setHelpOpen(!helpOpen)}>
      <AccordionItem>
        <AccordionButton fontWeight="bold">
          <Text>Help</Text> <AccordionIcon />
        </AccordionButton>
        <AccordionPanel>
          <Stack direction="column">
            <Table variant="compact" borderRadius="none">
              <Thead>
                <Tr>
                  <Th>Command</Th>
                  <Th>Description</Th>
                  <Th>Example</Th>
                </Tr>
              </Thead>
              <Tbody>
                <Tr>
                  <Td>* / + - ( )</Td>
                  <Td>Standard math</Td>
                  <Td>5 * 5 * (2 / 5)</Td>
                </Tr>
                <Tr>
                  <Td>in</Td>
                  <Td>Convert inches to centimeters</Td>
                  <Td>in 5</Td>
                </Tr>
                <Tr>
                  <Td>cm</Td>
                  <Td>Convert centimeters to inches</Td>
                  <Td>cm 5</Td>
                </Tr>
                <Tr>
                  <Td>kg</Td>
                  <Td>Convert kilograms to pounds</Td>
                  <Td>kg 5</Td>
                </Tr>
                <Tr>
                  <Td>lb</Td>
                  <Td>Convert pounds to kilograms</Td>
                  <Td>lb 5</Td>
                </Tr>
                <Tr>
                  <Td>inCBM</Td>
                  <Td>Calculate CBM from inches</Td>
                  <Td>incbm 20 40 50</Td>
                </Tr>
                <Tr>
                  <Td>cmCBM</Td>
                  <Td>Calculate CBM from centimeters</Td>
                  <Td>cmcbm 20 40 50</Td>
                </Tr>
                <Tr>
                  <Td>near</Td>
                  <Td>
                    Get nearby airports.
                    <br />
                    Second value is radius in miles (Default 250)
                  </Td>
                  <Td>
                    near lax
                    <br />
                    near lax 50
                  </Td>
                </Tr>
                <Tr>
                  <Td>ap</Td>
                  <Td>Lookup airports</Td>
                  <Td>
                    ap lax
                    <br />
                    ap lax lhr dxb
                  </Td>
                </Tr>
                <Tr>
                  <Td>zip</Td>
                  <Td>Lookup postal codes (500mi radius)</Td>
                  <Td>
                    zip 98253
                    <br />
                    zip DE 10318
                  </Td>
                </Tr>
                <Tr>
                  <Td>price</Td>
                  <Td>
                    Get price info for the last week on a route. Takes two additional arguments for chargeable weight
                    and specific airline. A CW of 0 checks all weights.
                  </Td>
                  <Td>
                    price mia bom
                    <br />
                    price atl dxb 500
                    <br />
                    price atl dxb 0 AC
                  </Td>
                </Tr>
                <Tr>
                  <Td>broadprice</Td>
                  <Td>
                    Get price info for the last week on a route, but check other airports around the origin. Same syntax
                    as "price"
                  </Td>
                  <Td whiteSpace="nowrap">
                    broadprice mia bom
                    <br />
                    broadprice atl dxb 500
                    <br />
                    broadprice atl dxb 0 AC
                  </Td>
                </Tr>
              </Tbody>
            </Table>
            <Stack spacing="0">
              <Text fontStyle="italic">If a field requires a space, surround it in quotes</Text>
              <Text fontStyle="italic">
                Example: <Code>zip AE "26810 85213"</Code>
              </Text>
            </Stack>
          </Stack>
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
};

const Calculator = ({ initialQuery = '', typesense }: { initialQuery?: string; typesense: TypesenseClient }) => {
  const [input, setInput] = useState(initialQuery);
  // Pretty much handles any output: elements, text, strings, etc
  const [result, setResult] = useState<any>('-');
  const { client } = trpc.useUtils();
  const [history, setHistory] = useLocalStorage<string[]>('calculator-history', []);

  const renderInput = useCallback(async () => {
    const calculatePromise = calculate(input.toLowerCase(), {
      typesenseClient: typesense,
      trpc: client,
    });
    setResult(<Spinner />);

    const calculated = await calculatePromise;
    if (calculated === null) {
      return setResult('-');
    }

    switch (typeof calculated) {
      case 'string':
      case 'number':
        return setResult(calculated);
      // eslint-disable-next-line no-fallthrough
      case 'object':
        // Double switchin'
        switch (calculated['type']) {
          case 'trackingInfo':
            return setResult(<TrackingInfo info={calculated['events']} />);
          case 'airports':
            return setResult(
              <NearbyAirports
                airport={calculated['list'][0]}
                nearby={calculated['list']?.length > 1 ? calculated['list'].slice(1) : [calculated['list'][0]]}
              />,
            );
          case 'path':
            return setResult(<AirportPath airports={calculated?.path} />);
          case 'priceEstimate':
            return setResult(<PriceEstimateDisplay estimate={calculated.result} />);
          case 'error':
            return setResult(<Text color="red">{calculated?.errorMessage}</Text>);
        }
      // eslint-disable-next-line no-fallthrough
      default:
        setResult('Unknown response, please report');
    }
  }, [client, input, typesense]);

  useEffect(() => {
    renderInput().catch((e) => {
      setResult(<Text color="red">{e.message}</Text>);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input]);

  const onKeyDown = useCallback(
    (event) => {
      if (event.key === 'Enter') {
        setHistory([input, ...history].slice(0, 10));
        if (typeof result === 'string' || typeof result === 'number') {
          navigator.clipboard.writeText(`${result}`);
        }
      }
    },
    [history, input, result, setHistory],
  );

  const onChange = useCallback(
    (event) => {
      const hasChanged = event.target.value !== input;
      if (hasChanged) setInput(event.target.value);
    },
    [input],
  );
  const inputRef = useRef<HTMLInputElement>();

  useEffect(() => {
    const focus = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.altKey || e.metaKey) {
        return;
      }
      inputRef?.current?.focus();
    };
    window.addEventListener('keydown', focus);
    return () => {
      window.removeEventListener('keydown', focus);
    };
  });

  return (
    <Flex flexGrow="1" p={0} direction="column" height="100vh" overflowY="auto">
      <Flex>
        <Flex w="50%" borderRight="1px solid" borderColor="gray.600" flexDir="column">
          <Input
            placeholder="18*2"
            size="lg"
            p={4}
            py={6}
            fontSize="xl"
            onChange={onChange}
            onKeyDown={onKeyDown}
            autoFocus
            border="none"
            value={input}
            ref={inputRef}
          />
          <Divider />
          <Flex direction="column">
            {history.map((str, i) => (
              <Flex
                pl="4"
                key={i}
                _even={{
                  bg: 'blackAlpha.200',
                }}
                _hover={{
                  bg: 'blackAlpha.400',
                }}
                py="1"
                onClick={() => setInput(str)}
              >
                {str}
              </Flex>
            ))}
          </Flex>
        </Flex>
        <Flex p={4} w="full">
          {result}
        </Flex>
      </Flex>
      <Help />
    </Flex>
  );
};
